import { useEffect, useState } from "react";
import { api, ApiError, type UsageResponse } from "../../lib/api";
import { fmtDateShort } from "../../lib/format";

function usd(v: number): string {
  return `$${v.toFixed(v < 1 ? 4 : 2)}`;
}

export default function SettingsUsage() {
  const [data, setData] = useState<UsageResponse | null | "error">(null);
  const [backing, setBacking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function load() {
    api
      .usage()
      .then(setData)
      .catch(() => setData("error"));
  }
  useEffect(load, []);

  async function backupNow() {
    setBacking(true);
    setMsg(null);
    try {
      const r = await api.runBackup();
      setMsg(`Backed up ${r.rows} rows → ${r.key}`);
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Backup failed (API ${e.status})` : (e as Error).message);
    } finally {
      setBacking(false);
    }
  }

  const d = data && data !== "error" ? data : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-title mb-4">AI usage &amp; cost</h2>
        <div className="card card-pad">
          {data === "error" ? (
            <p className="muted">Couldn't load usage.</p>
          ) : !d ? (
            <p className="muted">Loading…</p>
          ) : d.total.calls === 0 ? (
            <p className="muted">
              No AI calls yet. Once the Anthropic key is set, food-photo, voice and
              coaching usage (and estimated cost) show up here.
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <span className="field-label">Total estimated cost</span>
                <span className="font-display text-xl tabular-nums">{usd(d.total.cost)}</span>
              </div>
              <p className="muted mt-1 text-sm">
                {d.total.calls} calls · {d.total.input_tokens.toLocaleString()} in /{" "}
                {d.total.output_tokens.toLocaleString()} out tokens
              </p>

              <dl className="mt-4 divide-y divide-stone-200">
                {d.by_purpose.map((p) => (
                  <div key={p.purpose} className="flex items-center justify-between py-2">
                    <dt className="text-sm capitalize">{p.purpose.replace(/_/g, " ")}</dt>
                    <dd className="text-sm tabular-nums">
                      {p.calls} · {usd(p.cost)}
                    </dd>
                  </div>
                ))}
              </dl>

              {d.recent.length > 0 && (
                <details className="mt-3 text-xs text-ink-subtle">
                  <summary className="cursor-pointer">Recent calls</summary>
                  <ul className="mt-2 space-y-0.5">
                    {d.recent.map((r, i) => (
                      <li key={i}>
                        {fmtDateShort(r.called_at)} · {r.purpose.replace(/_/g, " ")} ·{" "}
                        {usd(r.cost_usd_estimate)}
                        {r.success === 0 ? " · failed" : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      </div>

      <div>
        <h2 className="section-title mb-4">Backups</h2>
        <div className="card card-pad">
          <p className="muted">
            A weekly job snapshots your data tables to R2 as JSON (Sundays). Run one
            now any time.
          </p>
          <p className="mt-3 text-sm">
            Last backup:{" "}
            <span className="tabular-nums">
              {d?.backup.last_at ? fmtDateShort(d.backup.last_at) : "never"}
            </span>
            {d?.backup.last_rows != null ? ` · ${d.backup.last_rows} rows` : ""}
          </p>
          <div className="mt-5">
            <button type="button" className="btn-secondary" onClick={backupNow} disabled={backing}>
              {backing ? "Backing up…" : "Back up now"}
            </button>
          </div>
          {msg && <p className="mt-3 text-sm text-ink-muted">{msg}</p>}
        </div>
      </div>
    </div>
  );
}
